import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../../../services/supabase';

const PostGrid = ({ profile, posts, setPosts, isOwnProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  
  const [openMenuPostId, setOpenMenuPostId] = useState(null);
  const [confirmDeletePostId, setConfirmDeletePostId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Function to correct URL case sensitivity
  const correctImageUrl = (url) => {
    if (!url) return null;
    
    // Fix the case sensitivity issue in the Supabase URL
    let correctedUrl = url;
    
    // Handle different case variations
    const incorrectPatterns = [
      /sttvwhschLpqwuLLowvb\.supabase\.co/g,
      /sttvwhschLpqwullowvb\.supabase\.co/g,
      /sttvwhschlpgwullowvb\.supabase\.co/g
    ];
    
    incorrectPatterns.forEach(pattern => {
      correctedUrl = correctedUrl.replace(pattern, 'sttvwhschlpgwullowvb.supabase.co');
    });
    
    return correctedUrl;
  };

  const handleCreatePost = () => {
    navigate("/new-post");
  };

  // Effect to handle clicks outside the menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest('.post-options-btn')) {
        setOpenMenuPostId(null);
      }
    };

    if (openMenuPostId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuPostId]);

  const handleOptionsClick = (postId, event) => {
    event.stopPropagation();
    setOpenMenuPostId(openMenuPostId === postId ? null : postId);
    setConfirmDeletePostId(null);
  };

  const handleEditClick = (postId) => {
    console.log("Edit clicked for post:", postId);
    setOpenMenuPostId(null);
    // Navigate to edit post page
    navigate(`/post/${postId}/edit`);
  };

  const handleDeleteClick = (postId) => {
    setConfirmDeletePostId(postId);
    setOpenMenuPostId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeletePostId) return;

    setLoading(true);
    try {
      // Delete associated images from storage first
      const { data: images, error: imagesError } = await supabase
        .from('post_media')
        .select('url, display_order')
        .eq('post_id', confirmDeletePostId)
        .eq('media_type', 'image')
        .order('display_order');

      if (imagesError) {
        console.error('Error fetching post images for deletion:', imagesError);
      } else if (images && images.length > 0) {
        const filePaths = images.map(img => {
          const urlParts = img.url.split('/post-images/');
          return urlParts.length > 1 ? `post-images/${urlParts[1]}` : null;
        }).filter(path => path !== null);

        if (filePaths.length > 0) {
          const { error: storageError } = await supabase.storage
            .from('post-images')
            .remove(filePaths);

          if (storageError) {
            console.error('Error deleting images from storage:', storageError);
          } else {
            console.log('Successfully deleted images from storage:', filePaths);
          }
        }
      }

      // Delete the post record from the database
      const { error: deleteError } = await supabase
        .from("posts")
        .delete()
        .eq("id", confirmDeletePostId);

      if (deleteError) {
        throw deleteError;
      }

      // Update the local state to remove the deleted post
      setPosts(posts.filter(post => post.id !== confirmDeletePostId));
      console.log("Post deleted successfully:", confirmDeletePostId);

    } catch (error) {
      console.error("Error deleting post:", error.message);
      alert("Failed to delete post. Please try again.");
    } finally {
      setConfirmDeletePostId(null);
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeletePostId(null);
  };



  const handlePostClick = (postId, event) => {
    if (event.target.closest('.post-options-container') || 
        event.target.closest('.post-options-menu')) {
      return;
    }
    
    navigate(`/post/${postId}`, { 
      state: { 
        previousPath: location.pathname,
        postData: posts.find(p => p.id === postId)
      } 
    });
  };

  return (
    <div className="posts-container">
      {/* Create Post Button - only show for own profile */}
      {isOwnProfile && (
        <button className="create-post-btn" onClick={handleCreatePost}>
          Create New Post
        </button>
      )}
      
      {posts.length > 0 ? (
        <div className="posts-grid">
          {posts.map((post) => (
            <div 
              key={post.id} 
              className="profile-post-card"
              onClick={(e) => handlePostClick(post.id, e)}
              style={{ cursor: 'pointer' }}
            >
              {/* Dedicated Header Section */}
              <div className="profile-post-card-header">
                {/* Title */}
                <h3 className="post-title">{post.title || "Untitled Post"}</h3>
                {/* Options Button - Only show for own posts */}
                {isOwnProfile && (
                  <div className="post-options-container">
                    <button
                      className="post-options-btn"
                      aria-label="Post options"
                      onClick={(e) => handleOptionsClick(post.id, e)}
                    >
                      ···
                    </button>
                    {/* Popup Menu */}
                    {openMenuPostId === post.id && (
                       <div className="post-options-menu" ref={menuRef}>
                        <button onClick={() => handleEditClick(post.id)} className="menu-item">Edit</button>
                        <button onClick={() => handleDeleteClick(post.id)} className="menu-item delete">Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Image Container */}
              <div className="post-image-container">
                {post.post_images && post.post_images.length > 0 ? (
                  <img
                    src={correctImageUrl(post.post_images[0].url)}
                    alt="Post"
                    className="profile-post-image"
                    onError={(e) => {
                      console.error("Image failed to load (from post_media):", post.post_images[0].url);
                      console.error("Post data:", post);
                      console.error("Post images array:", post.post_images);
                      // Try fallback to image_url if available
                      if (post.image_url && post.image_url !== post.post_images[0].url) {
                        console.log("Trying fallback image_url:", post.image_url);
                        e.target.src = correctImageUrl(post.image_url);
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                    onLoad={() => {
                      console.log("Image loaded successfully:", post.post_images[0].url);
                    }}
                  />
                ) : post.image_url ? (
                  <img 
                    src={correctImageUrl(post.image_url)} 
                    alt={post.title || 'Post image'} 
                    className="profile-post-image"
                    onError={(e) => {
                      console.error("Image failed to load:", post.image_url);
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200?text=Image";
                    }}
                  />
                ) : post.imageUrl ? ( 
                  <img 
                    src={correctImageUrl(post.imageUrl)} 
                    alt={post.title || 'Post image'} 
                    className="profile-post-image"
                    onError={(e) => {
                      console.error("Image failed to load (from imageUrl):", post.imageUrl);
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200?text=Image";
                    }}
                  />
                ) : post.map_data && post.map_data.thumbnail ? ( 
                  <img 
                    src={post.map_data.thumbnail} 
                    alt={post.title || 'Map thumbnail'} 
                    className="profile-post-image"
                    onError={(e) => {
                      console.error("Map thumbnail failed to load:", post.map_data.thumbnail);
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/300x200?text=Map";
                    }}
                  />
                ) : (
                  <div className="profile-post-image-placeholder">No Image</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-posts-message">
          <p>No posts yet</p>
          {isOwnProfile && (
            <p>Share your travel experiences with the world!</p>
          )}
        </div>
      )}

      {/* Confirmation Dialog Modal */}
      {confirmDeletePostId && (
        <div className="confirmation-dialog-overlay">
          <div className="confirmation-dialog">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this post?</p>
            <div className="dialog-buttons">
              <button onClick={handleCancelDelete} className="dialog-btn cancel">Cancel</button>
              <button onClick={handleConfirmDelete} className="dialog-btn confirm">Confirm</button>
            </div>
          </div>
        </div>
      )}
      

    </div>
  );
};

export default PostGrid; 
